import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GitBranch, Plus } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { SkeletonRegion, SkeletonText } from '@/components/Skeleton';
import { formatarData } from '@/lib/format';
import { Modal } from '@/features/tickets/Modal';
import {
  AvisoDeFalha,
  Chave,
  CLASSE_DE_CAMPO,
  EtiquetaDeVersao,
} from '@/features/workflow/componentes';
import { LinkDoFluxo } from '@/features/workflow/navegacao';
import { classificarFalha, type Falha } from '@/features/workflow/recusa';
import { chaveSugerida } from '@/features/workflow/regras';
import type { DefinicaoDeFluxo } from '@/features/workflow/tipos';
import { chavesDeFluxo, criarDefinicao, listarDefinicoes } from '@/features/workflow/workflow.api';

/**
 * O catálogo de fluxos.
 *
 * Uma linha por definição, com as versões dela à mostra: é a pergunta que
 * quem administra faz primeiro — *qual versão está no ar, e existe rascunho
 * esperando publicação?*. Esconder isso atrás de um clique transformaria a
 * lista num índice sem informação.
 *
 * Fica fora de `/administracao` porque aquela tela é dos **catálogos de
 * vocabulário** — situação, categoria, equipe —, listas planas que se editam
 * em linha. Um fluxo é um documento versionado com validação e publicação, e
 * empurrá-lo para dentro de uma aba de tabela daria a ele a aparência de mais
 * um cadastro.
 */
export function FluxosPage() {
  const [criando, setCriando] = useState(false);

  const consulta = useQuery({
    queryKey: chavesDeFluxo.definicoes,
    queryFn: listarDefinicoes,
  });

  return (
    <div className="mx-auto max-w-[1400px] space-y-3">
      <header className="flex items-center justify-between gap-4 border-b border-border pb-3">
        <h1 className="text-base font-semibold">Fluxos</h1>
        <Button
          size="sm"
          onClick={() => setCriando(true)}
          icon={<Plus className="h-4 w-4" aria-hidden />}
        >
          Novo fluxo
        </Button>
      </header>

      <Card>
        <CardBody className="p-0">
          {consulta.isError ? (
            <div className="px-5 py-6">
              <AvisoDeFalha
                falha={classificarFalha(consulta.error)}
                aoRecarregar={() => void consulta.refetch()}
              />
            </div>
          ) : consulta.isPending ? (
            <SkeletonRegion label="Carregando os fluxos" className="space-y-3 px-5 py-5">
              <SkeletonText lines={4} />
            </SkeletonRegion>
          ) : consulta.data.length === 0 ? (
            <div className="px-5 py-10">
              <EmptyState
                icon={<GitBranch className="h-7 w-7" aria-hidden />}
                title="Nenhum fluxo cadastrado"
                description="Um fluxo começa como definição vazia, ganha um rascunho em JSON e só entra em operação quando a publicação passa pela validação."
                action={
                  <Button size="sm" onClick={() => setCriando(true)}>
                    Criar o primeiro
                  </Button>
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-3 text-left font-medium">Fluxo</th>
                    <th className="px-4 py-3 text-left font-medium">Aplica-se a</th>
                    <th className="px-4 py-3 text-left font-medium">Versões</th>
                  </tr>
                </thead>
                <tbody>
                  {consulta.data.map((definicao) => (
                    <Linha key={definicao.id} definicao={definicao} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <DialogoDeNovoFluxo aberto={criando} aoFechar={() => setCriando(false)} />
    </div>
  );
}

function Linha({ definicao }: { definicao: DefinicaoDeFluxo }) {
  const publicada = definicao.versions.find((versao) => versao.status === 'PUBLISHED');
  const rascunho = definicao.versions.find((versao) => versao.status === 'DRAFT');

  return (
    <tr className="border-b border-border/60 transition-colors last:border-0 hover:bg-accent/40">
      <td className="px-4 py-3 align-top">
        <LinkDoFluxo
          definicaoId={definicao.id}
          className="font-medium text-foreground underline-offset-4 hover:text-primary hover:underline"
        >
          {definicao.name}
        </LinkDoFluxo>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <Chave valor={definicao.key} titulo="A chave não muda: é o que o grafo declara." />
          {definicao.active ? null : (
            <span className="text-xs text-muted-foreground">inativo</span>
          )}
        </div>
        {definicao.description ? (
          <p className="mt-1 max-w-lg text-xs text-muted-foreground">{definicao.description}</p>
        ) : null}
      </td>

      <td className="px-4 py-3 align-top">
        <Chave valor={definicao.entityType} />
      </td>

      <td className="px-4 py-3 align-top">
        {definicao.versions.length === 0 ? (
          <span className="text-xs text-muted-foreground">
            nenhuma — o fluxo ainda não tem grafo
          </span>
        ) : (
          <ul className="space-y-1">
            {publicada ? (
              <li className="flex items-center gap-2 text-xs">
                <EtiquetaDeVersao status="PUBLISHED" />
                <span className="tabular-nums">v{publicada.versionNumber}</span>
                <span className="text-muted-foreground">
                  desde {formatarData(publicada.publishedAt)}
                </span>
              </li>
            ) : null}
            {rascunho ? (
              <li className="flex items-center gap-2 text-xs">
                <EtiquetaDeVersao status="DRAFT" />
                <span className="tabular-nums">v{rascunho.versionNumber}</span>
                <span className="text-muted-foreground">esperando publicação</span>
              </li>
            ) : null}
            {/* Aposentadas contam, mas não ocupam uma linha cada: uma
                definição antiga pode ter dezenas, e nenhuma delas exige ação. */}
            {definicao.versions.some((versao) => versao.status === 'RETIRED') ? (
              <li className="text-xs text-muted-foreground">
                {definicao.versions.filter((versao) => versao.status === 'RETIRED').length}{' '}
                aposentada(s)
              </li>
            ) : null}
          </ul>
        )}
      </td>
    </tr>
  );
}

/**
 * A definição nasce vazia: chave, nome e tipo de entidade.
 *
 * O grafo vem depois, como rascunho, porque as duas coisas têm ritmos
 * diferentes — a chave é para sempre e o grafo muda a cada revisão do
 * processo.
 */
function DialogoDeNovoFluxo({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const cliente = useQueryClient();
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [entityType, setEntityType] = useState('ticket');
  const [description, setDescription] = useState('');
  const [falha, setFalha] = useState<Falha | null>(null);

  const criar = useMutation({
    mutationFn: () =>
      criarDefinicao({
        key: key.trim(),
        name: name.trim(),
        entityType: entityType.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
      }),
    onMutate: () => setFalha(null),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chavesDeFluxo.raiz });
      setKey('');
      setName('');
      setDescription('');
      aoFechar();
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo="Novo fluxo"
      descricao="A chave é o que o grafo, a integração e o modelo de chamado citam — e ela não muda depois."
    >
      <form
        className="space-y-4 px-5 py-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          criar.mutate();
        }}
      >
        <div className="space-y-1.5">
          <label htmlFor="fluxo-nome" className="block text-sm font-medium">
            Nome
          </label>
          <input
            id="fluxo-nome"
            value={name}
            onChange={(evento) => {
              setName(evento.target.value);
              // Sugere a chave enquanto ninguém a tiver digitado: é onde
              // nascem `mudanca`, `mudanca_2` e `mudanca-nova`.
              if (key === '' || key === chaveSugerida(name)) {
                setKey(chaveSugerida(evento.target.value));
              }
            }}
            className={CLASSE_DE_CAMPO}
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="fluxo-chave" className="block text-sm font-medium">
            Chave
          </label>
          <input
            id="fluxo-chave"
            value={key}
            onChange={(evento) => setKey(evento.target.value)}
            className={`${CLASSE_DE_CAMPO} font-mono`}
          />
          <p className="text-xs text-muted-foreground">
            Minúsculas, dígitos, ponto, hífen e sublinhado. O grafo precisa declarar a mesma chave.
          </p>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="fluxo-entidade" className="block text-sm font-medium">
            Aplica-se a
          </label>
          <input
            id="fluxo-entidade"
            value={entityType}
            onChange={(evento) => setEntityType(evento.target.value)}
            className={`${CLASSE_DE_CAMPO} font-mono`}
          />
          <p className="text-xs text-muted-foreground">
            Hoje só <code className="font-mono">ticket</code> tem contexto de expressão declarado no
            servidor. Outro tipo publica, mas os guards não são validados contra campo nenhum.
          </p>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="fluxo-descricao" className="flex items-baseline gap-1.5 text-sm font-medium">
            Descrição
            <span className="text-xs font-normal text-muted-foreground">opcional</span>
          </label>
          <textarea
            id="fluxo-descricao"
            rows={2}
            value={description}
            onChange={(evento) => setDescription(evento.target.value)}
            className={CLASSE_DE_CAMPO}
          />
        </div>

        {falha ? <AvisoDeFalha falha={falha} /> : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button
            type="submit"
            loading={criar.isPending}
            disabled={key.trim() === '' || name.trim().length < 2}
          >
            Criar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
